import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["countryKeys", "defaultCountry", "disabled", "disabledCountries", "invalid", "isCorrectNumber", "isRestoreCode", "label", "locale", "onBlur", "onChange", "onCountrySort", "onFocus", "placeholder", "searchable", "size", "subText", "success", "theme", "tooltip", "tooltipProps", "tooltipAbsolute", "value", "namespace"];
import React, { useMemo } from 'react';
import classNames from 'classnames/bind';
import { arrayOf, bool, func, node, object, oneOf, shape, string } from 'prop-types';
import { getMethodsForTestId } from "../utils/getMethodsForTestId";
import { getPhoneCountry, normalizePhone, normalizePhoneByCountry } from "../utils/phone";
import { onlyNumbers } from "../utils/string";
import { isArray } from "../utils/type";
import FlagIcon from '../FlagIcon';
import SelectField from '../SelectField';
import { generateOption } from '../SelectField/helpers';
import { defaultSort, getDefaultCountryKeys, getLocalByPhoneNumber, getMaskAsMap } from './helpers';
import styles from './PhoneField.css';
var cx = classNames.bind(styles);

var getPhoneOptionMaker = function getPhoneOptionMaker() {
  var disabledOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return function (_ref) {
    var country = _ref.country,
        name = _ref.name,
        code = _ref.code,
        valid = _ref.valid,
        validationsAcc = _ref.validationsAcc;
    var isDisabled = disabledOptions.includes(country.toLowerCase());
    return {
      left: /*#__PURE__*/React.createElement(FlagIcon, {
        code: country,
        className: cx('Option_flag', {
          Option_disabled: isDisabled
        })
      }),
      label: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: cx('Option_name', {
          Option_disabled: isDisabled
        })
      }, name), /*#__PURE__*/React.createElement("span", {
        className: cx('Option_code', {
          Option_disabled: isDisabled
        })
      }, code)),
      code: code,
      country: country,
      isDisabled: isDisabled,
      value: code,
      name: name,
      validations: validationsAcc ? [valid].concat(validationsAcc) : [valid]
    };
  };
};

var checkPhonePatterns = function checkPhonePatterns(input, patterns) {
  return isArray(patterns) ? patterns.some(function (item) {
    return new RegExp(item).test(input);
  }) : false;
};

var getPhoneValidator = function getPhoneValidator(_ref2) {
  var option = _ref2.option,
      setOption = _ref2.setOption,
      setInputValue = _ref2.setInputValue,
      isValid = _ref2.isValid,
      setIsValid = _ref2.setIsValid,
      maskMap = _ref2.maskMap,
      onChange = _ref2.onChange,
      currentValue = _ref2.currentValue;
  var phone = normalize(currentValue, option);
  var country = getCountry(phone, false, option);
  var optionValue = country && maskMap[country.toLowerCase()];
  var numberValidation = isValid;

  if (optionValue && checkPhonePatterns(onlyNumbers(phone), optionValue.validations) && !isValid) {
    setIsValid(true);
    numberValidation = true;
  } else if ((!optionValue || !checkPhonePatterns(onlyNumbers(phone), optionValue.validations)) && isValid) {
    setIsValid(false);
    numberValidation = false;
  }

  if (!optionValue || !phone && option) {
    setOption(null);
  } else if (!optionValue.isDisabled && country && (!option || option.country !== country.toLowerCase())) {
    setOption(maskMap[country.toLowerCase()]);
  }

  setInputValue(phone);
  onChange(phone, numberValidation, {
    code: option === null || option === void 0 ? void 0 : option.code,
    countryCode: option === null || option === void 0 ? void 0 : option.country
  });
};

var normalize = function normalize(number, countryOption) {
  return countryOption ? normalizePhoneByCountry(number, countryOption.country) : normalizePhone(number);
};

var getNumber = function getNumber(value, country) {
  return !value ? country.code : normalize(value, country);
};

var getCountry = function getCountry(number, withDefault, countryOption) {
  return getPhoneCountry(number, withDefault, countryOption ? countryOption.country : undefined);
};

var customSetter = function customSetter(_ref3) {
  var option = _ref3.option,
      currentValue = _ref3.value,
      inputValue = _ref3.inputValue,
      setInputValue = _ref3.setInputValue,
      setOption = _ref3.setOption,
      maskMap = _ref3.maskMap,
      defaultOption = _ref3.defaultOption;
  var number = getNumber(currentValue, option);
  var local = getLocalByPhoneNumber(number) || defaultOption;

  if (number !== currentValue || inputValue !== number) {
    if (currentValue) {
      setOption(generateOption({
        maskMap: maskMap,
        value: local
      }));
    }

    setInputValue(number);
  }
};

var defaultCountryKeys = getDefaultCountryKeys();

var PhoneField = function PhoneField(_ref4) {
  var countryKeys = _ref4.countryKeys,
      defaultCountry = _ref4.defaultCountry,
      disabled = _ref4.disabled,
      disabledCountries = _ref4.disabledCountries,
      invalid = _ref4.invalid,
      isCorrectNumber = _ref4.isCorrectNumber,
      isRestoreCode = _ref4.isRestoreCode,
      label = _ref4.label,
      locale = _ref4.locale,
      onBlur = _ref4.onBlur,
      onChange = _ref4.onChange,
      onCountrySort = _ref4.onCountrySort,
      onFocus = _ref4.onFocus,
      placeholder = _ref4.placeholder,
      searchable = _ref4.searchable,
      size = _ref4.size,
      subText = _ref4.subText,
      success = _ref4.success,
      theme = _ref4.theme,
      tooltip = _ref4.tooltip,
      tooltipProps = _ref4.tooltipProps,
      tooltipAbsolute = _ref4.tooltipAbsolute,
      value = _ref4.value,
      namespace = _ref4.namespace,
      restProps = _objectWithoutProperties(_ref4, _excluded);

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'phoneField'),
      getNamespace = _getMethodsForTestId.getNamespace;

  var options = useMemo(function () {
    var keys = countryKeys || defaultCountryKeys;
    return keys.map(function (key) {
      return {
        value: key
      };
    });
  }, [countryKeys]);
  return /*#__PURE__*/React.createElement(SelectField, _extends({
    namespace: getNamespace(),
    options: options,
    defaultOption: defaultCountry,
    disabled: disabled,
    disabledOptions: disabledCountries,
    invalid: invalid,
    isCorrectInput: isCorrectNumber,
    isRestoreCode: isRestoreCode,
    label: label,
    locale: locale,
    onBlur: onBlur,
    onChange: onChange,
    onOptionSort: onCountrySort,
    onFocus: onFocus,
    placeholder: placeholder,
    searchable: searchable,
    size: size,
    subText: subText,
    success: success,
    theme: theme,
    tooltip: tooltip,
    tooltipProps: tooltipProps,
    tooltipAbsolute: tooltipAbsolute,
    value: value,
    isReflectOptionValue: true,
    isValidateOnBlur: false,
    getMaskMap: getMaskAsMap,
    getValidator: getPhoneValidator,
    getOptionMaker: getPhoneOptionMaker,
    customSetter: customSetter,
    variant: "phone"
  }, restProps));
};

export var sizes = ['small'];
export var themes = ['b2c', 'b2b'];
export var locales = ['en', 'ru', 'it', 'es', 'de', 'zh'];
PhoneField.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Уникальное название для атрибута `data-test`, более подробная информация [здесь](/story/intro-разработчикам-расстановка-data-test--page)
   */
  namespace: string,

  /**
   * Ключи для отображения стран
   */
  countryKeys: arrayOf(string),

  /**
   * Выбранная страна по умолчанию
   */
  defaultCountry: string,

  /**
   * Состояние disabled
   */
  disabled: bool,

  /**
   * Блокировать выбор определенных стран
   */
  disabledCountries: arrayOf(string),

  /**
   * Состояние когда номер не валиден
   */
  invalid: bool,

  /**
   * Корректный ввод номера телефона
   */
  // eslint-disable-next-line react/boolean-prop-naming
  isCorrectNumber: bool,

  /**
   * Восстанавливать код города, если он был удален
   */
  // eslint-disable-next-line react/boolean-prop-naming
  isRestoreCode: bool,

  /**
   * Заголовок поля ввода
   */
  label: node,

  /**
   * Текущая локаль
   */
  locale: oneOf(locales),

  /**
   * Обратный вызов onBlur
   */
  onBlur: func,

  /**
   * Обратный вызов onChange
   */
  onChange: func,

  /**
   * Обратный вызов для сортировки опций
   */
  onCountrySort: func,

  /**
   * Обратный вызов onFocus
   */
  onFocus: func,

  /**
   * Placeholder для поля ввода
   */
  placeholder: node,

  /**
   * Поиск при выборе страны
   */
  searchable: bool,

  /**
   * Размер поля
   */
  size: oneOf(sizes),

  /**
   * Подпись под полем ввода
   */
  subText: node,

  /**
   * Валидное состояние поля
   */
  success: bool,

  /**
   * Тема отображения
   */
  theme: oneOf(themes),

  /**
   * Поясняющий текст под полем ввода
   */
  tooltip: node,

  /**
   * Дополнительные параметры для tooltip
   */
  tooltipProps: object,

  /**
   * Абсолютное позиционирование tooltip
   */
  tooltipAbsolute: bool,

  /**
   * Значение поля ввода
   */
  value: string,

  /**
   * Classes API
   */
  classes: shape({
    root: string,
    container: string,
    inner: string,
    input: string,
    label: string,
    icon: string
  })
} : {};
PhoneField.defaultProps = {
  label: '',
  theme: 'b2c',
  placeholder: 'Country',
  onCountrySort: defaultSort,
  value: ''
};
export default PhoneField;